import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { Form, Icon, Row, Col, Steps } from 'antd';
import BasePage from 'components/BasePage';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import FormDatePicker from 'components-desktop/FormDatePicker';
import FormInput from 'components-desktop/FormInput';
import FormSelect from 'components-desktop/FormSelect';
import FormTextArea from 'components-desktop/FormTextArea';
import Panel from 'components-desktop/Panel';
import helpers from 'misc/helpers';

const propTypes = {
  form: PropTypes.object.isRequired,
};

@autobind
class AddNewPatientPage extends BasePage {
  constructor(props) {
    super(props);

    this.state = {
      currentStep: 1,
      isContinueButtonEnable: true,
    };
  }

  componentDidMount() {
  }

  getTitle() {
    return 'Patient Application';
  }

  async addNewPatient(values) {
    try {
      this.setState({
        isContinueButtonEnable: false,
      });

      const params = {
        firstName: helpers.extraFormInputValue(values, 'firstName'),
        middleName: helpers.extraFormInputValue(values, 'middleName'),
        lastName: helpers.extraFormInputValue(values, 'lastName'),
        gender: helpers.extraFormSelectValue(values, 'gender'),
        birth: helpers.extraFormDateValue(values, 'birth'),
        language: helpers.extraFormInputValue(values, 'language'),
        street: helpers.extraFormInputValue(values, 'street'),
        city: helpers.extraFormInputValue(values, 'city'),
        state: helpers.extraFormInputValue(values, 'state'),
        zipCode: helpers.extraFormInputValue(values, 'zipCode'),
        homePhone: helpers.extraFormInputValue(values, 'homePhone'),
        cellPhone: helpers.extraFormInputValue(values, 'cellPhone'),
        medicaidNo: helpers.extraFormInputValue(values, 'medicaidNo'),
        medicareNo: helpers.extraFormInputValue(values, 'medicareNo'),
        emergContName: helpers.extraFormInputValue(values, 'emergContName'),
        emergContRelation: helpers.extraFormInputValue(values, 'emergContRelation'),
        emergContNumber: helpers.extraFormInputValue(values, 'emergContNumber'),
        diagnosis: helpers.extraFormTextAreaValue(values, 'diagnosis'),
        noteFromPatient: helpers.extraFormTextAreaValue(values, 'noteFromPatient'),
      };
      const patientId = await this.callServiceForLong('/patient/add', params);

      this.notifySuccess('Success', 'A new patient has been added.');

      // Go to the new patient's profile page.
      setTimeout(() => {
        window.location = `/patients/patient_profile?id=${patientId}`;
      }, 2000);
    } finally {
      this.setState({
        isContinueButtonEnable: true,
      });
    }
  }

  onBackClick() {
    if (this.state.currentStep > 1) {
      this.setState((prevState, props) => ({
        currentStep: prevState.currentStep - 1,
      }));
    }
  }

  onContinueClick() {
    const { currentStep } = this.state;

    let validateFields = null;
    if (currentStep === 1) {
      validateFields = ['firstName', 'middleName', 'lastName', 'gender', 'birth', 'language'];
    } else if (currentStep === 2) {
      validateFields = ['street', 'city', 'state', 'zipCode', 'homePhone', 'cellPhone'];
    } else if (currentStep === 3) {
      validateFields = ['medicaidNo', 'medicareNo', 'emergContName', 'emergContRelation',
        'emergContNumber', 'diagnosis', 'noteFromPatient'];
    }

    if (currentStep < 4) {
      this.props.form.validateFieldsAndScroll(validateFields, (err, values) => {
        if (!err) {
          this.setState((prevState, props) => ({
            currentStep: prevState.currentStep + 1,
          }));
        }
      });
    } else {
      this.props.form.validateFieldsAndScroll((err, values) => {
        if (!err) {
          this.addNewPatient(values);
        }
      });
    }
  }

  getStepStatus(step) {
    const { currentStep } = this.state;

    if (step === currentStep) {
      return 'process';
    } else if (step < currentStep) {
      return 'finish';
    } else {
      return 'wait';
    }
  }

  validateAtLeastOnePhone(rule, value, callback, source, options) {
    const { form } = this.props;

    if (!value) {
      const otherValue = form.getFieldValue(rule.field === 'homePhone' ? 'cellPhone' : 'homePhone');

      if (!otherValue) {
        callback('At least one phone is required.');
      } else {
        callback();
      }
    } else {
      callback();
    }
  }

  renderContent() {
    const { form } = this.props;
    const { currentStep, isContinueButtonEnable } = this.state;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
        alignItems="center"
      >
        <Panel
          width="match_parent"
          height="wrap_content"
          margin="0 0 24px 0"
          color="blue_steel"
          title={`Patient Application - Step ${currentStep} of 4`}
          icon="user"
        >
          <styledComp.AntdForm
            width="match_parent"
            height="wrap_content"
            // onSubmit={this.handleSubmit}
          >
            {/* Content */}
            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
            >
              <FlexColContainer
                width="match_parent"
                height="wrap_content"
                margin="0 0 16px 0"
                padding="16px 32px"
              >
                <Steps
                  type="default"
                  current={currentStep}
                  // onChange={this.onChange}
                >
                  <Steps.Step title="Personal Details" status={this.getStepStatus(1)} />
                  <Steps.Step title="Contact Info" status={this.getStepStatus(2)} />
                  <Steps.Step title="Patient Details" status={this.getStepStatus(3)} />
                  <Steps.Step title="Agree and Submit" status={this.getStepStatus(4)} />
                </Steps>
              </FlexColContainer>

              {/* Content */}
              <FlexColContainer
                width="match_parent"
                height="wrap_content"
              >
                <styledComp.Span
                  width="wrap_content"
                  height="wrap_content"
                  margin="0 0 28px 0"
                  color="textPrimaryDark"
                  fontSize="24px"
                  fontWeight="300"
                >
                  {currentStep === 1 && 'Personal details needed for this application'}
                  {currentStep === 2 && 'Contact information needed for this application'}
                  {currentStep === 3 && 'Patient details needed for this application'}
                  {currentStep === 4 && 'Confirm and submit your application'}
                </styledComp.Span>

                {/* Step 1 */}
                <styledComp.Div
                  width="match_parent"
                  height="wrap_content"
                  display={currentStep !== 1 ? 'none' : undefined}
                >
                  <Row type="flex" gutter={24}>
                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="firstName"
                        label="First Name"
                        placeholder=""
                        isRequired
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="middleName"
                        label="Middle Name (Optional)"
                        placeholder=""
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="lastName"
                        label="Last Name"
                        placeholder=""
                        isRequired
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                      <FormSelect
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="gender"
                        label="Gender"
                        placeholder="Select..."
                        options={['Female', 'Male']}
                        isRequired
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                      <FormDatePicker
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="birth"
                        label="Date of Birth"
                        placeholder="mm/dd/yyyy"
                        extra="Select date from calendar"
                        isRequired
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="language"
                        label="Language"
                        placeholder="English"
                        extra="Patient's preferred language"
                        isRequired
                      />
                    </Col>
                  </Row>
                </styledComp.Div>

                {/* Step 2 */}
                <styledComp.Div
                  width="match_parent"
                  height="wrap_content"
                  display={currentStep !== 2 ? 'none' : undefined}
                >
                  <Row type="flex" gutter={24}>
                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 12 }} lg={{ span: 12 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="street"
                        label="Street address and Suite or Apt. #"
                        placeholder="123 Main Street, Apt. 103"
                        extra="We currently only accept applications from New York."
                        isRequired
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="city"
                        label="City"
                        placeholder=""
                        isRequired
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 2 }} lg={{ span: 2 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="state"
                        label="State"
                        placeholder=""
                        disabled
                        initialValue="NY"
                        isRequired
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 4 }} lg={{ span: 4 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="zipCode"
                        label="ZIP Code"
                        placeholder=""
                        isRequired
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="homePhone"
                        label="Home Phone"
                        placeholder="(111) 111-1111"
                        inputMask="(999) 999-9999"
                        validator={this.validateAtLeastOnePhone}
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="cellPhone"
                        label="Cell Phone"
                        placeholder="(111) 111-1111"
                        inputMask="(999) 999-9999"
                        validator={this.validateAtLeastOnePhone}
                      />
                    </Col>
                  </Row>
                </styledComp.Div>

                {/* Step 3 */}
                <styledComp.Div
                  width="match_parent"
                  height="wrap_content"
                  display={currentStep !== 3 ? 'none' : undefined}
                >
                  <Row type="flex" gutter={24}>
                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} lg={{ span: 8 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="medicaidNo"
                        label="Medicaid No."
                        placeholder=""
                        isRequired
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} lg={{ span: 8 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="medicareNo"
                        label="Medicare No. (Optional)"
                        placeholder=""
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} lg={{ span: 8 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="emergContName"
                        label="Emergency Contact Person (Optional)"
                        placeholder=""
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} lg={{ span: 8 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="emergContRelation"
                        label="Emergency Contact Relationship (Optional)"
                        placeholder=""
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} lg={{ span: 8 }}>
                      <FormInput
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="emergContNumber"
                        label="Emergency Contact Phone Number (Optional)"
                        placeholder=""
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                      <FormTextArea
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="diagnosis"
                        label="Diagnosis (For example, why do you need a personal assistant for your daily life) (Optional)"
                        rows={3}
                        max={4000}
                        extra="To help us approve your application, please briefly explain why a personal assistant is needed."
                      />
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                      <FormTextArea
                        width="match_parent"
                        height="wrap_content"
                        form={form}
                        name="noteFromPatient"
                        label="Additional Message to Our Staff (Optional)"
                        rows={3}
                        max={2000}
                        extra="Any additional information that you want to send to our staff about this application."
                      />
                    </Col>
                  </Row>
                </styledComp.Div>

                {/* Step 4 */}
                <styledComp.Div
                  width="match_parent"
                  height="wrap_content"
                  display={currentStep !== 4 ? 'none' : undefined}
                >
                </styledComp.Div>
              </FlexColContainer>
            </FlexColContainer>

            {/* Footer */}
            <FlexRowContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
              justifyContent="center"
              backgroundColor="containerGrayBackground"
            >
              {currentStep > 1 &&
                <styledComp.AntdButton
                  width="wrap_content"
                  height="32px"
                  margin="0 4px 0 0"
                  fontSize="14px"
                  type="default"
                  onClick={this.onBackClick}
                >
                  <Icon type="left-circle" />
                  BACK
                </styledComp.AntdButton>
              }

              <CustomButton
                width="wrap_content"
                height="32px"
                backgroundColor="blue_steel"
                fontSize="14px"
                type="primary"
                loading={!isContinueButtonEnable}
                onClick={this.onContinueClick}
              >
                {currentStep < 4 ? 'CONTINUE' : 'SUBMIT'}
                <Icon type="right-circle" />
              </CustomButton>
            </FlexRowContainer>
          </styledComp.AntdForm>
        </Panel>
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
AddNewPatientPage.propTypes = propTypes;

export default Form.create()(AddNewPatientPage);
